import { useEffect, useState } from 'react';
import { listPhoneNumbers } from '../api/agent';
import { fetchRegistrations } from '../api/registrations';
import { api } from '../lib/client';

const PAGE_SIZE = 200;

/**
 * Loads registration endpoints allowed for outbound originate (outbound=true).
 */
async function fetchRegistrationEndpointsForOriginate(handler) {
  const items = [];
  let nextOffset = 0;

  do {
    const params = {
      type: 'phone-registration',
      originate: 'true',
      offset: nextOffset,
      pageSize: PAGE_SIZE,
    };
    if (handler?.trim()) params.handler = handler.trim();
    const { data } = await api.get('/phone-endpoints', { params });
    items.push(...(data.items || []));
    nextOffset = data.nextOffset != null ? data.nextOffset : null;
  } while (nextOffset != null);

  return items;
}

/**
 * @param {string | null | undefined} agentHandler - e.g. livekit from model prefix
 * @param {'deploy' | 'originate'} purpose - deploy lists all allocated endpoints; originate lists outbound-capable only
 * @param {boolean} enabled - when false, skips fetch
 */
export default function useTelephonyCallerEndpoints(agentHandler, purpose, enabled = true) {
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled) {
      setPhoneNumbers([]);
      setRegistrations([]);
      return;
    }
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const handler = agentHandler?.trim() || undefined;
        const phonesPromise =
          purpose === 'originate'
            ? listPhoneNumbers(true, handler)
            : listPhoneNumbers(false, handler);
        const regsPromise =
          purpose === 'originate'
            ? fetchRegistrationEndpointsForOriginate(handler)
            : fetchRegistrations().then((rows) => {
                const list = Array.isArray(rows) ? rows : [];
                return handler
                  ? list.filter((r) => !r.handler || String(r.handler).trim() === handler)
                  : list;
              });
        const [phones, regs] = await Promise.all([phonesPromise, regsPromise]);
        if (cancelled) return;
        setPhoneNumbers(Array.isArray(phones) ? phones : []);
        setRegistrations(Array.isArray(regs) ? regs : []);
      } catch (err) {
        if (!cancelled) {
          setError(err?.message || String(err));
          setPhoneNumbers([]);
          setRegistrations([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [agentHandler, purpose, enabled]);

  return { phoneNumbers, registrations, loading, error };
}
